import { useRef, useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { trackFormStart, trackFormSuccess } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const improveOptions = [
    "More qualified leads",
    "Better Google Ads",
    "Faster follow-up",
    "More sales from enquiries",
];
const budgetOptions = ["Under £500", "£500–£1,000", "£1,000–£2,500", "£2,500+"];
const reviewOptions = ["Yes", "Not yet"];

const inputClass =
    "w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-gray-900 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#2563EB]";
const labelClass = "mb-1 block text-sm font-medium text-[#0B1F44]";

const Chip = ({ selected, onClick, testId, children }) => (
    <button
        type="button"
        data-testid={testId}
        aria-pressed={selected}
        onClick={onClick}
        className={`rounded-full border px-4 py-2 text-sm font-medium transition-all ${
            selected
                ? "border-[#2563EB] bg-[#2563EB] text-white"
                : "border-gray-200 bg-white text-[#374151] hover:border-[#2563EB]/50 hover:text-[#2563EB]"
        }`}
    >
        {children}
    </button>
);

const initialForm = {
    name: "",
    company: "",
    email: "",
    phone: "",
    website: "",
    business_type: "",
    improve: "",
    budget: "",
    growth_review: "",
};

export const EnquiryForm = () => {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");
    const startedRef = useRef(false);

    const handleFormStart = () => {
        if (!startedRef.current) {
            startedRef.current = true;
            trackFormStart();
        }
    };

    const set = (key) => (e) =>
        setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!form.improve || !form.budget || !form.growth_review) {
            setError("Please answer all three questions before submitting.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            trackFormSuccess();
            setSubmitted(true);
        } catch (err) {
            setError(
                "Something went wrong sending your details. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="growth-review"
            data-testid="enquiry-section"
            className="scroll-mt-16 bg-[#F8FAFC] py-24 lg:py-32"
        >
            <div className="mx-auto max-w-3xl px-6 lg:px-8">
                <Reveal>
                    <div className="text-center">
                        <p className="text-sm font-semibold uppercase tracking-wider text-[#2563EB]">
                            Get Started
                        </p>
                        <h2 className="mt-3 text-3xl font-bold tracking-tight text-[#0B1F44] lg:text-4xl">
                            Request your 20-minute Growth Review.
                        </h2>
                        <p className="mt-4 text-base text-[#4B5563]">
                            Tell us a little about your business and we will be
                            in touch shortly.
                        </p>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="fp-card-shadow mt-12 rounded-3xl border border-gray-100 bg-white p-8 sm:p-10">
                        {submitted ? (
                            <div
                                data-testid="enquiry-success-message"
                                className="flex flex-col items-center py-10 text-center"
                            >
                                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#2563EB]/10">
                                    <CheckCircle2 className="h-7 w-7 text-[#2563EB]" />
                                </span>
                                <h3 className="mt-6 text-xl font-semibold text-[#0B1F44]">
                                    Thanks. We've received your details and
                                    will be in touch shortly.
                                </h3>
                            </div>
                        ) : (
                            <form
                                data-testid="enquiry-form"
                                onSubmit={handleSubmit}
                                onFocusCapture={handleFormStart}
                                noValidate={false}
                            >
                                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="enquiry-name" className={labelClass}>
                                            Name
                                        </label>
                                        <input
                                            id="enquiry-name"
                                            data-testid="enquiry-name-input"
                                            required
                                            value={form.name}
                                            onChange={set("name")}
                                            className={inputClass}
                                            placeholder="Your name"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="enquiry-company" className={labelClass}>
                                            Company
                                        </label>
                                        <input
                                            id="enquiry-company"
                                            data-testid="enquiry-company-input"
                                            required
                                            value={form.company}
                                            onChange={set("company")}
                                            className={inputClass}
                                            placeholder="Company name"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="enquiry-email" className={labelClass}>
                                            Email
                                        </label>
                                        <input
                                            id="enquiry-email"
                                            data-testid="enquiry-email-input"
                                            type="email"
                                            required
                                            value={form.email}
                                            onChange={set("email")}
                                            className={inputClass}
                                            placeholder="you@company.co.uk"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="enquiry-phone" className={labelClass}>
                                            Phone
                                        </label>
                                        <input
                                            id="enquiry-phone"
                                            data-testid="enquiry-phone-input"
                                            type="tel"
                                            required
                                            value={form.phone}
                                            onChange={set("phone")}
                                            className={inputClass}
                                            placeholder="07xxx xxx xxx"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="enquiry-website" className={labelClass}>
                                            Website
                                        </label>
                                        <input
                                            id="enquiry-website"
                                            data-testid="enquiry-website-input"
                                            value={form.website}
                                            onChange={set("website")}
                                            className={inputClass}
                                            placeholder="www.yourcompany.co.uk"
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="enquiry-business-type" className={labelClass}>
                                            Business Type
                                        </label>
                                        <input
                                            id="enquiry-business-type"
                                            data-testid="enquiry-business-type-input"
                                            required
                                            value={form.business_type}
                                            onChange={set("business_type")}
                                            className={inputClass}
                                            placeholder="e.g. Plumbing, Legal, Dental"
                                        />
                                    </div>
                                </div>

                                <fieldset className="mt-8">
                                    <legend className="text-sm font-medium text-[#0B1F44]">
                                        What do you want to improve?
                                    </legend>
                                    <div className="mt-3 flex flex-wrap gap-2.5">
                                        {improveOptions.map((o, i) => (
                                            <Chip
                                                key={o}
                                                testId={`improve-option-${i}`}
                                                selected={form.improve === o}
                                                onClick={() => set("improve")(o)}
                                            >
                                                {o}
                                            </Chip>
                                        ))}
                                    </div>
                                </fieldset>

                                <fieldset className="mt-7">
                                    <legend className="text-sm font-medium text-[#0B1F44]">
                                        Monthly marketing budget
                                    </legend>
                                    <div className="mt-3 flex flex-wrap gap-2.5">
                                        {budgetOptions.map((o, i) => (
                                            <Chip
                                                key={o}
                                                testId={`budget-option-${i}`}
                                                selected={form.budget === o}
                                                onClick={() => set("budget")(o)}
                                            >
                                                {o}
                                            </Chip>
                                        ))}
                                    </div>
                                </fieldset>

                                <fieldset className="mt-7">
                                    <legend className="text-sm font-medium text-[#0B1F44]">
                                        Would you like a 20-minute Growth
                                        Review?
                                    </legend>
                                    <div className="mt-3 flex flex-wrap gap-2.5">
                                        {reviewOptions.map((o, i) => (
                                            <Chip
                                                key={o}
                                                testId={`review-option-${i}`}
                                                selected={form.growth_review === o}
                                                onClick={() => set("growth_review")(o)}
                                            >
                                                {o}
                                            </Chip>
                                        ))}
                                    </div>
                                </fieldset>

                                {error && (
                                    <p
                                        data-testid="enquiry-error-message"
                                        className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600"
                                    >
                                        {error}
                                    </p>
                                )}

                                <button
                                    type="submit"
                                    data-testid="enquiry-submit-button"
                                    disabled={submitting}
                                    className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2563EB] px-7 py-3.5 text-base font-semibold text-white transition-all hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-600/20 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            Request My Growth Review
                                            <ArrowRight className="h-4 w-4" />
                                        </>
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
